// Client/src/pages/digi/Invoice/TemplateDesigner/TemplateDesignerPage.jsx
//
// Full-screen invoice template designer (route /invoice-templates/:id, vendors).
// Layout: top bar; left rail (Elements, Fields, Layers, Images, Theme); the page
// canvas; the inspector with Checks below it. Everything edits one draft design
// (useDesignState) which autosaves; Submit freezes it as a version for the
// companies to approve. Spec: docs/superpowers/specs/2026-11-02-vendor-invoice-templates-design.md (Part 3).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams, Prompt } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";
import MetaTags from "react-meta-tags";
import useDesignState from "./useDesignState";
import TopBar from "./TopBar";
import SubmitModal from "./SubmitModal";
import ChecksPanel from "./ChecksPanel";
import Canvas from "./canvas/Canvas";
import Inspector from "./inspector/Inspector";
import LeftRail from "./rail/LeftRail";
import ElementsPanel from "./rail/ElementsPanel";
import LayersPanel from "./rail/LayersPanel";
import ThemePanel from "./rail/ThemePanel";
import { FieldsPanel, AssetsPanel } from "./rail/AssetsAndFields";
import {
  PX_PER_MM,
  contentSize,
  locate,
  findBand,
  makeElement,
  insertElement,
  updateElement,
  updateBand,
  updatePage,
  removeElement,
  duplicateElement,
  shiftElement,
  moveElement,
  addBand,
  removeBand,
  duplicateBand,
  shiftBand,
  convertBand,
  countElements,
  LIMITS,
  fieldLabelMap,
  fieldKindMap,
  docFieldGroups,
} from "./designModel";
import {
  getTemplateFields,
  getSampleInvoices,
  listTemplates,
  updateTemplate,
  submitTemplate,
  previewTemplatePdf,
  uploadTemplateAsset,
  removeTemplateAsset,
} from "../Components/templateApi";
import "./designer.scss";

const BACK_URL = "/invoices?tab=templates";
const NARROW_PX = 1024;

function currentRole() {
  try {
    const token = localStorage.getItem("accessToken");
    return token ? String(jwt_decode(token)?.role || "").toLowerCase() : "";
  } catch {
    return "";
  }
}

const isTyping = (target) => {
  const tag = target?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable;
};

export default function TemplateDesignerPage() {
  const { id } = useParams();
  const history = useHistory();
  const role = useMemo(currentRole, []);
  const ds = useDesignState(id);
  const { draft, change } = ds;

  const [selection, setSelection] = useState(null);
  const [railTab, setRailTab] = useState("elements");
  const [zoom, setZoom] = useState(1);
  const [sample, setSample] = useState("demo");
  const [samples, setSamples] = useState([]);
  const [fields, setFields] = useState(null);
  const [listRow, setListRow] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [renderState, setRenderState] = useState({ rendering: false, error: "" });
  const [assetUrls, setAssetUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [narrow, setNarrow] = useState(() => window.innerWidth < NARROW_PX);
  const [focusText, setFocusText] = useState(false);
  const [submittedJson, setSubmittedJson] = useState(null);
  const canvasWrap = useRef(null);
  const fittedOnce = useRef(false);

  const readOnly = narrow || ds.status === "conflict";

  // ------------------------------------------------------------ loading

  useEffect(() => {
    getTemplateFields().then(setFields).catch(() => setFields({ groups: [], dateFormats: [], moneyFormats: [], numberFormats: [] }));
    getSampleInvoices().then((s) => setSamples(s || [])).catch(() => {});
  }, []);

  const loadListRow = useCallback(async () => {
    try {
      const rows = await listTemplates({ includeArchived: 1 });
      setListRow((rows || []).find((r) => String(r._id) === String(id)) || null);
    } catch {
      /* the top bar falls back to the template's own approvals */
    }
  }, [id]);
  useEffect(() => {
    loadListRow();
  }, [loadListRow]);

  useEffect(() => {
    const v = ds.template?.versions;
    if (v && submittedJson === null) setSubmittedJson(v.length ? JSON.stringify(v[v.length - 1].design) : "");
  }, [ds.template, submittedJson]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < NARROW_PX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Release object URLs made for freshly uploaded image thumbnails (on leave).
  const assetUrlsRef = useRef(assetUrls);
  assetUrlsRef.current = assetUrls;
  useEffect(() => () => Object.values(assetUrlsRef.current).forEach((u) => URL.revokeObjectURL(u)), []);

  // ------------------------------------------------------------ derived

  const cs = draft ? contentSize(draft.page) : { w: 190, h: 260, pageW: 215.9 };
  const fieldGroups = useMemo(() => fields?.groups || [], [fields]);
  const ctx = useMemo(
    () => ({
      palette: draft?.page.palette || {},
      fieldGroups,
      docFieldGroups: docFieldGroups(fieldGroups),
      labels: fieldLabelMap(fieldGroups),
      kinds: fieldKindMap(fieldGroups),
      formats: {
        dateFormats: fields?.dateFormats || [],
        moneyFormats: fields?.moneyFormats || [],
        numberFormats: fields?.numberFormats || [],
      },
      assets: ds.template?.assets || [],
      contentWidth: cs.w,
      onUpload: (file) => uploadRef.current(file),
      notify: (m) => toast.info(m),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft?.page.palette, fieldGroups, fields, ds.template?.assets, cs.w]
  );

  const approvals = listRow?.approvals || ds.template?.approvals || [];
  const unsubmitted = draft && submittedJson !== null ? JSON.stringify(draft) !== submittedJson : false;
  const errors = ds.issues.filter((i) => i.level === "error");
  const warnings = ds.issues.filter((i) => i.level === "warning");
  const submitBlockedReason = errors.length
    ? `Fix ${errors.length} problem${errors.length === 1 ? "" : "s"} in Checks first`
    : ds.status === "conflict"
      ? "Reload — this template changed in another tab"
      : "";

  // ------------------------------------------------------------ actions

  const select = useCallback((s) => {
    setSelection(s);
    setFocusText(false);
  }, []);

  // Where a new element goes: next to the selected element (same list), else
  // into the selected section, else the last section that accepts it.
  const targetFor = useCallback(
    (type) => {
      if (!draft) return null;
      const loc = selection?.kind === "element" ? locate(draft, selection.id) : null;
      if (loc && !(type === "row" && (loc.col || loc.band.kind === "free"))) {
        return { bandId: loc.band.id, colId: loc.col?.id, index: loc.index + 1, free: loc.band.kind === "free" && !loc.col };
      }
      const band = selection?.kind === "band" ? findBand(draft, selection.id) : loc?.band;
      if (band && !(type === "row" && band.kind === "free")) return { bandId: band.id, free: band.kind === "free" };
      const candidates = draft.bands.filter((b) => !(type === "row" && b.kind === "free"));
      const last = candidates[candidates.length - 1];
      return last ? { bandId: last.id, free: last.kind === "free" } : null;
    },
    [draft, selection]
  );

  const addElement = useCallback(
    (type, opts = {}) => {
      if (!draft) return;
      if (countElements(draft) >= LIMITS.elements) {
        toast.error(`A template can have at most ${LIMITS.elements} elements`);
        return;
      }
      let target = targetFor(type);
      let base = draft;
      if (!target) {
        const r = addBand(draft, "flow");
        base = r.design;
        target = { bandId: r.id, free: false };
      }
      const band = findBand(base, target.bandId);
      const at = target.free ? { x: 6 + (band.elements.length % 8) * 4, y: 4 + (band.elements.length % 8) * 3 } : undefined;
      const el = makeElement(type, target.free, { ...opts, at });
      change(insertElement(base, el, target.free ? { bandId: target.bandId, frame: el.frame } : target));
      setSelection({ kind: "element", id: el.id });
      if (type === "text") setFocusText(true);
    },
    [draft, change, targetFor]
  );

  const actions = useMemo(
    () => ({
      select,
      updateElement: (elId, patch, ck) => change((d) => updateElement(d, elId, patch), { coalesce: ck ? `${elId}:${ck}` : undefined }),
      removeElement: (elId) => {
        change((d) => removeElement(d, elId));
        setSelection(null);
      },
      duplicateElement: (elId) => {
        let newId = null;
        change((d) => {
          const r = duplicateElement(d, elId);
          newId = r.id;
          return r.design;
        });
        if (newId) setSelection({ kind: "element", id: newId });
      },
      shiftElement: (elId, dir) => change((d) => shiftElement(d, elId, dir)),
      moveElement: (elId, target) => change((d) => moveElement(d, elId, target)),
      updateBand: (bandId, patch, ck) => change((d) => updateBand(d, bandId, patch), { coalesce: ck ? `${bandId}:${ck}` : undefined }),
      convertBand: (bandId, kind) => change((d) => convertBand(d, bandId, kind, contentSize(d.page).w)),
      removeBand: (bandId) => {
        const band = findBand(draft, bandId);
        if (band?.elements.length && !window.confirm(`Delete "${band.label}" and everything in it?`)) return;
        change((d) => removeBand(d, bandId));
        setSelection(null);
      },
      duplicateBand: (bandId) => {
        let newId = null;
        change((d) => {
          const r = duplicateBand(d, bandId);
          newId = r.id;
          return r.design;
        });
        if (newId) setSelection({ kind: "band", id: newId });
      },
      shiftBand: (bandId, dir) => change((d) => shiftBand(d, bandId, dir)),
      addBand: (kind) => {
        let newId = null;
        const loc = selection?.kind === "element" ? locate(draft, selection.id) : null;
        const after = selection?.kind === "band" ? draft.bands.findIndex((b) => b.id === selection.id) : loc ? loc.bandIndex : draft.bands.length - 1;
        change((d) => {
          const r = addBand(d, kind, after + 1);
          newId = r.id;
          return r.design;
        });
        if (newId) setSelection({ kind: "band", id: newId });
      },
    }),
    [change, select, draft, selection]
  );

  const uploadAsset = async (file) => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      toast.error("Upload a PNG, JPEG or WebP image");
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Images can be at most 5 MB");
      return null;
    }
    setUploading(true);
    try {
      const before = new Set((ds.template?.assets || []).map((a) => a.id));
      const assets = await uploadTemplateAsset(id, file);
      ds.patchTemplate({ assets });
      const added = (assets || []).find((a) => !before.has(a.id));
      if (added) setAssetUrls((u) => ({ ...u, [added.id]: URL.createObjectURL(file) }));
      toast.success("Image uploaded");
      return added || null;
    } catch {
      return null;
    } finally {
      setUploading(false);
    }
  };

  const uploadRef = useRef(uploadAsset);
  uploadRef.current = uploadAsset;

  const pickImageAndAdd = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/webp";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const asset = await uploadAsset(file);
      if (asset) addElement("image", { assetId: asset.id });
    };
    input.click();
  };

  const onFieldPick = (field) => {
    const loc = selection?.kind === "element" ? locate(draft, selection.id) : null;
    if (field.key.startsWith("line.")) {
      toast.info(`"${field.label}" is a line item value — add it as a column in the line items table.`);
      return;
    }
    if (loc?.el.type === "text") {
      change((d) => updateElement(d, loc.el.id, (e) => ({ ...e, runs: [...e.runs, { field: field.key }] })));
    } else {
      addElement("text", { runs: [{ field: field.key }] });
    }
  };

  const fit = useCallback(() => {
    const w = canvasWrap.current?.clientWidth || 900;
    const stageW = cs.pageW * PX_PER_MM + 240;
    setZoom(Math.max(0.4, Math.min(2, Math.floor(((w - 32) / stageW) * 100) / 100)));
  }, [cs.pageW]);

  useEffect(() => {
    if (draft && !fittedOnce.current && canvasWrap.current) {
      fittedOnce.current = true;
      fit();
    }
  }, [draft, fit]);

  const previewPdf = async () => {
    // Open the tab now (inside the click) so pop-up blockers allow it.
    const win = window.open("", "_blank");
    setPreviewBusy(true);
    try {
      await ds.flush();
      const url = await previewTemplatePdf(id, draft, sample);
      if (win) win.location.href = url;
      else window.location.assign(url);
    } catch {
      win?.close();
    } finally {
      setPreviewBusy(false);
    }
  };

  const submit = async (companyIds) => {
    const ok = await ds.flush();
    if (!ok) {
      toast.error("Your latest changes are not saved yet — try again in a moment");
      return;
    }
    try {
      const row = await submitTemplate(id, companyIds);
      setListRow(row);
      setSubmittedJson(JSON.stringify(draft));
      setSubmitOpen(false);
      toast.success("Sent for approval. You'll get an email when it's reviewed.");
    } catch {
      /* toast shown by the API helper */
    }
  };

  // ------------------------------------------------------------ keyboard

  useEffect(() => {
    if (!draft || readOnly) return undefined;
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z" && !isTyping(e.target)) {
        e.preventDefault();
        if (e.shiftKey) ds.redo();
        else ds.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === "y" && !isTyping(e.target)) {
        e.preventDefault();
        ds.redo();
        return;
      }
      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        ds.flush();
        return;
      }
      if (isTyping(e.target) || document.querySelector(".modal.show")) return;
      if (e.key === "Escape") {
        setSelection(null);
        return;
      }
      if (selection?.kind === "band") {
        if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          actions.removeBand(selection.id);
        }
        return;
      }
      const loc = selection?.kind === "element" ? locate(draft, selection.id) : null;
      if (!loc) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        actions.removeElement(loc.el.id);
      } else if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        actions.duplicateElement(loc.el.id);
      } else if (e.key.startsWith("Arrow")) {
        e.preventDefault();
        if (loc.el.locked) return;
        const step = e.shiftKey ? 5 : 1;
        if (loc.el.frame) {
          const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
          const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
          change(
            (d) => updateElement(d, loc.el.id, (el) => ({ ...el, frame: { ...el.frame, x: Math.round((el.frame.x + dx) * 10) / 10, y: Math.round((el.frame.y + dy) * 10) / 10 } })),
            { coalesce: `${loc.el.id}:nudge` }
          );
        } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          change((d) => shiftElement(d, loc.el.id, e.key === "ArrowUp" ? -1 : 1));
        }
      } else if (e.key === "Enter" && loc.el.type === "text") {
        e.preventDefault();
        setFocusText(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft, selection, readOnly, ds, actions, change]);

  // ------------------------------------------------------------ render

  if (role && role !== "vendor") {
    return (
      <div className="td-message">
        <FeatherIcon icon="lock" size={26} />
        <p>Invoice templates are designed by vendors from their own login.</p>
        <button type="button" className="td-btn td-btn--primary" onClick={() => history.push("/invoices")}>
          Back to invoices
        </button>
      </div>
    );
  }

  if (ds.loadError) {
    return (
      <div className="td-message">
        <FeatherIcon icon="alert-triangle" size={26} />
        <p>{ds.loadError}</p>
        <button type="button" className="td-btn td-btn--primary" onClick={() => history.push(BACK_URL)}>
          Back to templates
        </button>
      </div>
    );
  }

  if (!draft || !fields) {
    return (
      <div className="td-message" role="status">
        <FeatherIcon icon="loader" size={24} className="td-spin" />
        <p>Opening the designer…</p>
      </div>
    );
  }

  const selLoc = selection?.kind === "element" ? locate(draft, selection.id) : null;
  const targetLabel = (() => {
    const t = targetFor("text");
    return t ? findBand(draft, t.bandId)?.label : null;
  })();

  return (
    <div className="td-root">
      <MetaTags>
        <title>Rentdigicare | {ds.template?.name || "Invoice template"}</title>
      </MetaTags>
      <Prompt when={ds.hasUnsaved} message="Your latest changes are still saving. Leave anyway?" />
      <TopBar
        name={ds.template?.name || ""}
        onRename={async (name) => {
          try {
            await updateTemplate(id, { name });
            ds.patchTemplate({ name });
          } catch {
            /* toast shown */
          }
        }}
        approvals={approvals}
        unsubmitted={unsubmitted}
        status={ds.status}
        canUndo={ds.canUndo}
        canRedo={ds.canRedo}
        onUndo={ds.undo}
        onRedo={ds.redo}
        zoom={zoom}
        onZoom={setZoom}
        onFit={fit}
        sample={sample}
        samples={samples}
        onSample={setSample}
        onPreviewPdf={previewPdf}
        previewBusy={previewBusy}
        onSubmit={() => setSubmitOpen(true)}
        submitBlockedReason={submitBlockedReason}
        onBack={() => history.push(BACK_URL)}
        rendering={renderState.rendering}
        readOnly={readOnly}
      />

      {ds.status === "conflict" && (
        <div className="td-banner td-banner--danger" role="alert">
          <FeatherIcon icon="alert-triangle" size={15} />
          This template was changed in another tab or window, so editing here is paused to avoid overwriting it.
          <button type="button" className="td-btn td-btn--ghost td-btn--sm" onClick={() => ds.reload()}>
            Reload the latest version
          </button>
        </div>
      )}
      {narrow && (
        <div className="td-banner" role="note">
          <FeatherIcon icon="monitor" size={15} /> Open the designer on a larger screen (at least 1024 px wide) to edit. This is a read-only preview.
        </div>
      )}

      <div className={`td-body${readOnly ? " is-readonly" : ""}`}>
        {!readOnly && (
          <LeftRail tab={railTab} onTab={setRailTab}>
            {railTab === "elements" && (
              <ElementsPanel onAdd={addElement} onAddBand={actions.addBand} onUploadImage={pickImageAndAdd} targetLabel={targetLabel} />
            )}
            {railTab === "fields" && <FieldsPanel fieldGroups={fieldGroups} onPick={onFieldPick} selectedIsText={selLoc?.el.type === "text"} />}
            {railTab === "layers" && <LayersPanel design={draft} selection={selection} actions={actions} />}
            {railTab === "assets" && (
              <AssetsPanel
                assets={ds.template?.assets || []}
                assetUrls={assetUrls}
                uploading={uploading}
                onUpload={uploadAsset}
                onAddToPage={(a) => addElement("image", { assetId: a.id })}
                onUseAsBackground={(a) => change((d) => updatePage(d, (p) => ({ ...p, background: { ...(p.background || {}), assetId: a.id, opacity: p.background?.opacity ?? 0.15 } })))}
                onRemove={async (a) => {
                  const used = JSON.stringify(draft).includes(`"${a.id}"`);
                  if (!window.confirm(used ? `"${a.filename || "This image"}" is used on the page. Remove it anyway?` : `Remove "${a.filename || "this image"}"?`)) return;
                  try {
                    const assets = await removeTemplateAsset(id, a.id);
                    ds.patchTemplate({ assets });
                  } catch {
                    /* toast shown */
                  }
                }}
              />
            )}
            {railTab === "theme" && (
              <ThemePanel
                page={draft.page}
                assets={ds.template?.assets || []}
                onPage={(fn, ck) => change((d) => updatePage(d, fn), { coalesce: ck ? `page:${ck}` : undefined })}
                numbering={ds.template?.numbering}
                onNumbering={async (n) => {
                  const cur = ds.template?.numbering || {};
                  if (cur.prefix === n.prefix && cur.nextNo === n.nextNo && (cur.pad || 0) === (n.pad || 0)) return;
                  try {
                    const res = await updateTemplate(id, { numbering: n });
                    ds.patchTemplate({ numbering: res.numbering });
                  } catch {
                    /* toast shown */
                  }
                }}
              />
            )}
          </LeftRail>
        )}

        <main className="td-canvas" ref={canvasWrap} aria-label="Invoice page" onPointerDown={(e) => e.target === e.currentTarget && select(null)}>
          <Canvas
            templateId={id}
            design={draft}
            sample={sample}
            zoom={zoom}
            selection={selection}
            onSelect={select}
            change={change}
            readOnly={readOnly}
            ctx={ctx}
            onRenderState={setRenderState}
          />
        </main>

        {!readOnly && (
          <div className="td-side">
            <Inspector design={draft} selection={selection} ctx={ctx} actions={actions} focusText={focusText} />
            <ChecksPanel
              issues={ds.issues}
              onPick={(i) => {
                if (i.elementId && locate(draft, i.elementId)) select({ kind: "element", id: i.elementId });
                else if (i.bandId) select({ kind: "band", id: i.bandId });
              }}
            />
          </div>
        )}
      </div>

      <SubmitModal isOpen={submitOpen} onClose={() => setSubmitOpen(false)} onSubmit={submit} approvals={approvals} warnings={warnings.length} />
    </div>
  );
}
